import { Box, Typography } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { get, kebabCase } from 'lodash';
import React, { ChangeEvent } from 'react';
import { translate } from '../../../../../../common/intl';
import { Autocomplete, Button, FormikField } from '../../../../../components/forms/fields-next';
import { Mode, MODE_FIELD } from '../../../../../components/KendoDataGrid/constants';
import OverflowTooltip from '../../../../../components/OverflowTooltip';
import { isConfigurationStagesMapped } from '../helpers';
import { CustomCellProps, OptionType } from '../types';

const Editor: React.FC<CustomCellProps> = ({
  dataItem,
  dataIndex,
  field,
  dataOptions,
  onChange,
  onConfigureFieldMapping,
  onDocumentTypeUpdated,
}) => {
  const { getFieldProps, setFieldValue, errors } = useFormikContext();
  const editField = `${field}_edit`;

  const isDisabled = isConfigurationStagesMapped(dataItem);

  const handleChange = (event: ChangeEvent<unknown>, value: OptionType[]) => {
    if (isDisabled) {
      return;
    }
    setFieldValue(editField, value);
    onDocumentTypeUpdated?.(value);
    onChange?.({
      dataIndex,
      dataItem,
      syntheticEvent: event,
      field,
      value,
    });
  };

  return (
    <Box>
      <FormikField name={editField} error={get(errors, field)}>
        <Autocomplete
          {...getFieldProps(editField)}
          blurOnSelect
          disableClearable={isDisabled}
          multiple
          getOptionLabel={(option: OptionType) => option.text}
          getOptionSelected={(option: OptionType, value: OptionType) =>
            option.id === value.id
          }
          placeholder={translate('common.select')}
          options={dataOptions}
          defaultValue={dataItem[editField] ?? []}
          data-cy={`${kebabCase(field)}-field`}
          onChange={handleChange}
          size="small"
          disabled={isDisabled}
        />
      </FormikField>
      {[Mode.edit, Mode.add].includes(dataItem._mode) && <Button
        variant="text"
        size="small"
        onClick={(e) => onConfigureFieldMapping?.(dataItem)}
        style={{ padding: 0 }}
      >
        Configure Field Mapping
      </Button>}
    </Box>
  );
};

export const DocumentTypeCell: React.FC<CustomCellProps> = (props) => {
  const { field, dataItem, onClick } = props;
  const handleClick = () => onClick?.({ dataItem });
  const dataCy = kebabCase(field);

  const data = dataItem[field] as OptionType[];
  const dataToShow = (data ?? []).map(item => item.text).join(', ');

  if ([Mode.add, Mode.edit].includes(dataItem[MODE_FIELD])) {
    return <Editor {...props} />;
  }

  return (
    <Box
      display="flex"
      justifyContent="start"
      data-cy={`${dataCy}-td`}
      onClick={handleClick}
    >
      <OverflowTooltip
        arrow
        placement="top"
        title={dataToShow}
        onClick={handleClick}
        data-cy={`${dataCy}-value`}
      >
        <Typography
          variant="subtitle2"
          color="textPrimary"
        >
          {dataToShow}
        </Typography>
      </OverflowTooltip>
    </Box>
  );
};
